/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */
import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import {
    string, object, bool, func,
} from 'prop-types';

// Material UI
import { Tooltip, ClickAwayListener, Grid, Dialog, DialogContent, DialogTitle, IconButton } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import ThumbDownOutlinedIcon from '@material-ui/icons/ThumbDownOutlined';
import ThumbUpOutlinedIcon from '@material-ui/icons/ThumbUpOutlined';

// Helpers
import mbpLogger from 'mbp-logger';
import EventDispatcher from '../state/reducers/EventDispatcher';

// Assets
import Spinner from '../assets/svg/Spinner';
import fallbackImgURL from '../assets/svg/FallbackImage';


const styles = (theme) => ({
    closeIcon: {
        position: 'absolute',
        right: '3%',
        top: '1.5%',
        cursor: 'pointer',
    },
    contentWrapper: {
        maxWidth: 'calc(750px - 24px)',
        minWidth: 'auto',
        maxHeight: 'calc(750px - 48px)',
        minHeight: 'calc(750px - 48px)',
        overflowY: 'hidden',
        paddingBottom: '24px',
        [theme.breakpoints.down('sm')]: {
            maxWidth: 'calc(350px - 48px)',
            maxHeight: 'calc(450px - 48px)',
            minHeight: 'calc(350px - 48px)',
            minWidth: 'calc(330px - 24px)',
            overflowY: 'hidden',
            paddingBottom: '24px',
        },
    },
    landscapeContentWrapper: {
        maxWidth: 'calc(750px - 48px)',
        minWidth: 'calc(750px - 48px)',
        maxHeight: 'calc(750px - 48px)',
        minHeight: 'auto',
        overflowY: 'hidden',
        paddingBottom: '24px',
        [theme.breakpoints.down('sm')]: {
            maxWidth: 'calc(350px - 48px)',
            maxHeight: 'calc(450px - 48px)',
            minHeight: 'auto',
            minWidth: 'calc(350px - 48px)',
            overflowY: 'hidden',
            paddingBottom: '24px',
        },
    },
    innerContentWrapper: {
        overflowY: 'hidden',
        height: 'calc(700px - 24px)',
        width: 'calc(500px - 24px)',
        [theme.breakpoints.down('sm')]: {
            height: 'calc(376px - 24px)',
            width: 'auto',
        },
        position: 'relative',
    },
    landscapeInnerContentWrapper: {
        overflowY: 'hidden',
        height: 'auto',
        [theme.breakpoints.down('sm')]: {
            height: 'auto',
        },
    },
    showImg: {
        display: 'block',
    },
    hideImg: {
        display: 'none',
    },
    centerSpinner: {
        position: 'absolute',
        top: '50%',
        left: '50%',
    },
    thumbs: {
        position: 'absolute',
        bottom: '7px',
        background: theme.palette.white || '#fff',
        color: theme.palette.colorPrimaryAccountCtaButton,
        borderRadius: '50%',
        padding: '5px',
        cursor: 'pointer',
        '&:hover': {
            background: theme.palette.colorPrimaryAccountCtaButton,
            color: theme.palette.white,
        },
        '&.Mui-disabled': {
            color: 'rgba(0, 0, 0, 0.26)',
            backgroundColor: theme.palette.common.grey,
        },
    },
    thumbsUp: {
        right: '61px',
    },
    thumbsDown: {
        right: '13px',
    },
    toolTip: {
        '& .mbp-account-MuiTooltip-tooltip': {
            color: theme.palette.black || '#000',
            backgroundColor: theme.palette.white || '#fff',
            '& .mbp-account-MuiTooltip-arrow': {
                color: theme.palette.white || '#fff',
            },
        },
    },
});

const DeliveryImageModal = ({
    classes, deliveryImageLink, openModal, closeModal, trackingNumber, isDeliveryImageThumbsEnabled, recipientZipCode, thumbsUpCall,
}) => {
    // Set default width and height to support vertical image
    const [imageSize, setImageSize] = useState({ width: '100%', height: '100%' });
    const [isLandscape, setIsLandscape] = useState(false);
    const [link, setLink] = useState(deliveryImageLink);
    const [loading, setLoading] = useState(true);
    const [toolTipOpen, setToolTip] = useState(false);
    const [thumbsDownToolTipOpen, setThumbsDownToolTip] = useState(false);
    const [isThumbsDisabled, setThumbsDisabled] = useState(false);

    // Grab image ref
    let image = null;
    image = React.createRef();

    // Change image width and height if landscape
    const loadImg = (img) => {
        const deliveryimg = img.current;
        // Check if image is landscape
        if (deliveryimg.naturalHeight < deliveryimg.naturalWidth) {
            setImageSize({ height: 'auto', width: '100%' });
            setIsLandscape(true);
            setLoading(false);
        }
        setLoading(false);
    };

    const setThumbsUp = (thumbUp) => {
        if (thumbUp === 'U') {
            setToolTip(true);
        } else {
            setThumbsDownToolTip(true);
        }
        setThumbsDisabled(true);
        
        const payload = {
            thumbUp,
            trackingNumber,
            recipientZipCode,
        };
        thumbsUpCall(payload);
    };

    const handleTooltipClose = () => {
        setToolTip(false);
    };

    const thumbsDownTooltipClose = () => {
        setThumbsDownToolTip(false);
    };

    useEffect(() => {
        setLink(deliveryImageLink);
    }, [isLandscape]);

    return (
        <Dialog maxWidth="md" open={openModal}>
            <DialogTitle className={classes.mainHeader}>
                <Grid item className={classes.closeIconWrapper}>
                    <CloseIcon className={classes.closeIcon} onClick={() => closeModal()} data-testid="close-button" />
                </Grid>
            </DialogTitle>
            <DialogContent className={isLandscape ? `${classes.landscapeContentWrapper}` : `${classes.contentWrapper}`}>
                <div className={isLandscape ? `${classes.landscapeInnerContentWrapper}` : `${classes.innerContentWrapper}`}>
                    {loading && <div className={classes.centerSpinner}><Spinner /></div>}
                    {isDeliveryImageThumbsEnabled
                        && (
                        <>
                            <ClickAwayListener onClickAway={handleTooltipClose}>
                                <Tooltip
                                    PopperProps={{disablePortal: true}} onClose={handleTooltipClose} open={toolTipOpen} classes={{ popper: classes.toolTip }}
                                    arrow disableFocusListener disableHoverListener disableTouchListener title="Thank you for the feedback!"
                                >
                                    <IconButton
                                        disabled={isThumbsDisabled}
                                        className={`${classes.thumbsUp} ${classes.thumbs}`}
                                        onClick={() => setThumbsUp('U')}
                                    >
                                        <ThumbUpOutlinedIcon />
                                    </IconButton>
                                </Tooltip>
                            </ClickAwayListener>
                            <ClickAwayListener onClickAway={thumbsDownTooltipClose}>
                                <Tooltip
                                    PopperProps={{disablePortal: true}} onClose={thumbsDownTooltipClose} open={thumbsDownToolTipOpen} classes={{ popper: classes.toolTip }}
                                    arrow disableFocusListener disableHoverListener disableTouchListener title="Thank you for the feedback!"
                                >
                                    <IconButton
                                        className={`${classes.thumbsDown} ${classes.thumbs}`}
                                        disabled={isThumbsDisabled}
                                        onClick={() => setThumbsUp('D')}
                                    >
                                       <ThumbDownOutlinedIcon />
                                    </IconButton>
                                </Tooltip>
                            </ClickAwayListener>
                        </>)
                    } 
                    <img
                        className={loading ? classes.hideImg : classes.showImg}
                        width={imageSize.width}
                        height={imageSize.height}
                        key={link}
                        src={link}
                        alt="delivery link"
                        ref={image}
                        onLoad={() => {
                            loadImg(image);
                        }}
                        onError={(e) => {
                            if (e.target.src !== fallbackImgURL) {
                                e.target.src = fallbackImgURL;
                                mbpLogger.logError({
                                    appName: process.env.npm_package_name,
                                    component: 'DeliveryImageModal.js',
                                    message: `Error loading Delivery Hub image: ${link}`,
                                    module: 'DeliveryImageModal',
                                });
                            }
                        }}
                    />
                </div>
            </DialogContent>
        </Dialog>
    );
};

DeliveryImageModal.defaultProps = {
    deliveryImageLink: '',

};

DeliveryImageModal.propTypes = {
    classes: object.isRequired,
    openModal: bool.isRequired,
    closeModal: func.isRequired,
    deliveryImageLink: string,
};

const mapDispatchToProps = (dispatch) => ({
    thumbsUpCall: (payload) => dispatch(EventDispatcher.setThumbsUpForDeliveryImage(payload)),
});



export default connect(null, mapDispatchToProps)(withStyles(styles)(DeliveryImageModal));
